import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

interface FormData {
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  address: string;
}

const CreateSuperAdmin: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({
    email: '',
    username: '',
    firstName: '',
    lastName: '',
    address: '',
  });

  const generateUsername = (firstName: string, lastName: string): string => {
    // Remove any non-alphanumeric characters and convert to lowercase
    const cleanFirstName = firstName.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
    const cleanLastName = lastName.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
    
    // Combine first and last name
    return `${cleanFirstName}${cleanLastName}`;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };
      
      // If first name or last name changes, update username
      if (name === 'firstName' || name === 'lastName') {
        const firstName = name === 'firstName' ? value : prev.firstName;
        const lastName = name === 'lastName' ? value : prev.lastName;
        newData.username = generateUsername(firstName, lastName);
      }
      
      return newData;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      // Create the super admin user with a temporary password
      const userResponse = await axios.post(
        `${API_BASE_URL}/users`,
        {
          email: formData.email,
          username: formData.username,
          firstName: formData.firstName,
          lastName: formData.lastName,
          address: formData.address,
          role: 'Super Admin',
          password: 'TemporaryPassword123!'
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      // Check if user was created successfully
      if (userResponse.data.userId) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const userId = userResponse.data.userId;
        
        // Send invitation after user is created
        try {
          const invitationResponse = await axios.post(
            `${API_BASE_URL}/invitations/send`,
            {
              email: formData.email,
              firstName: formData.firstName,
              lastName: formData.lastName
            },
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );

          if (invitationResponse.data.success) {
            setSuccess(`Super Admin ${formData.firstName} ${formData.lastName} created successfully. Invitation sent to ${formData.email}`);
            setFormData({
              email: '',
              username: '',
              firstName: '',
              lastName: '',
              address: '',
            });
          }
        } catch (invitationError: any) {
          console.error('Error sending invitation:', invitationError);
          setError('User created but failed to send invitation. Please try sending the invitation again from the settings page.');
        }
      } else {
        setError('Failed to create user. Please try again.');
      }
    } catch (error: any) {
      console.error('Error creating user:', error);
      
      // Handle specific error cases
      if (error.response) {
        const statusCode = error.response.status;
        const errorData = error.response.data;
        
        // Log detailed error for debugging
        console.log('Server response:', {
          status: statusCode,
          data: errorData,
          headers: error.response.headers
        });
        
        if (statusCode === 409) {
          setError(`A user with email ${formData.email} already exists in the system. Please use a different email address.`);
        } else if (statusCode === 500) {
          let errorMsg = 'A server error occurred. The development team has been notified.';
          
          // Try to extract more specific error information if available
          if (errorData && typeof errorData === 'object') {
            if (errorData.error) errorMsg += ` Error: ${errorData.error}`;
            if (errorData.message) errorMsg += ` ${errorData.message}`;
            if (errorData.detail) errorMsg += ` Details: ${errorData.detail}`;
          }
          
          setError(errorMsg);
        } else if (errorData?.message) {
          setError(errorData.message);
        } else {
          setError(`Failed to create Super Admin (Error ${statusCode}). Please check your input and try again.`);
        }
      } else if (error.request) {
        // Request was made but no response was received
        setError('No response received from server. Please check your internet connection and try again.');
      } else {
        // Something happened in setting up the request
        setError(`Request failed: ${error.message}. Please contact support if the issue persists.`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
        <IconButton
          onClick={() => navigate('/owner/settings')}
          color="primary"
          aria-label="back to settings"
        >
          <ArrowBackIcon />
        </IconButton>
      </Box>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Create Super Admin User
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Username"
                name="username"
                value={formData.username}
                InputProps={{
                  readOnly: true,
                }}
                helperText="Automatically generated from first and last name"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                required
                multiline
                rows={3}
                placeholder="Full address including street, city, and postal code"
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" paragraph>
                This user will be created with the <strong>Super Admin</strong> role, which has full access to manage the platform.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                An invitation email will be sent to the provided email address.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                sx={{ mt: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Create Super Admin'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default CreateSuperAdmin; 